// src/pages/product/AddProduct.tsx
//
// AddProduct
// - Admin page to create a new product.
// - Compact, single-column layout (maxWidth=900).
// - Page title/breadcrumb/back are handled by route handle.pageHeader (no AppPageHeader slot injection here).
//
// Responsibilities
// - Collect product fields across 3 sections (Basic / Specification / Settings).
// - Validate required fields before submit.
// - If stock supplier requires API parameters, guide the operator with a JSON template.
//
// UX rules
// - Cancel uses shared confirm dialog to prevent accidental navigation.
// - Success/failure feedback uses shared systemAlert (no window.alert/confirm).
//
// Data shape note
// - UI uses flattened Country objects for productCoverages.
// - API expects productCoverages as { countryId }[] and is converted on submit.

import { useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Close, SaveOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ProductStatus, RequiredDataType } from '@models/product';
import {
  createProduct,
  ProductSaleType,
  ProductType,
  ProductUpsertRequest,
} from '@apis/product';
import { getStock, StockSupplierTaskType } from '@apis/stock';

import { useAllStockList } from '@queries/stocks/useAllStockList';
import { useCountryList } from '@queries/countries/useCountryList';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { alertMissingValues, isNullOrValidJson } from '@utils/common';
import { generateProductCode } from '@utils/code';

import ProductBasicInfoSection from '@components/product/ProductBasicInfoSection';
import ProductSpecificationSection from '@components/product/ProductSpecificationSection';
import ProductSettingsSection from '@components/product/ProductSettingsSection';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type Country = {
  countryId: number;
  englishName: string;
};

/**
 * Local form type:
 * - productCoverages is kept as Country[] for UI convenience (Autocomplete, chips, labels).
 * - Converted to API shape ({ countryId }[]) on submit.
 */
type AddProductForm = Omit<ProductUpsertRequest, 'productCoverages'> & {
  productCoverages: Country[];
};

type StockOption = {
  value: number;
  label: string;
};

const ACTION_BTN_SX = {
  whiteSpace: 'nowrap',
  width: { xs: '100%', sm: 160 },
  flex: { sm: '0 0 auto' },
} as const;

const AddProduct = () => {
  const navigate = useNavigate();

  // Shared confirm + alert (no window.confirm/alert)
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------

  const [form, setForm] = useState<AddProductForm>({
    productCode: generateProductCode(),
    productSaleType: ProductSaleType.REAL_TIME,
    productType: ProductType.ESIM,
    productName: '',
    description: '',
    price: 0,
    specification: {
      dayCount: 0,
      isUnlimitedData: true,
      isVoiceCallSupported: false,
      dataCapacity: {
        quantity: 0,
        unit: 'GB',
        refreshPeriodUnit: 'NEVER',
        refreshPeriodValue: 0,
      },
      isFup: false,
    },
    productCoverages: [],
    requiredData: { requiredFields: [] },
    productStatus: ProductStatus.ACTIVE,
    apiParameter: undefined,
    stockId: 0,
  });

  // Whether API parameter is required (depends on stock supplier type)
  const [isApiInputNeeded, setIsApiInputNeeded] = useState(false);

  // Default JSON template for apiParameter (shown + optionally prefilled)
  const [defaultApiParameter, setDefaultApiParameter] = useState<string | null>(null);

  // Stock selector options
  const [stockOptionList, setStockOptionList] = useState<StockOption[]>([]);

  // ---------------------------------------------------------------------------
  // External data (stock list / country list)
  // ---------------------------------------------------------------------------

  const { data: stocksData } = useAllStockList();
  const { data: countriesData } = useCountryList();

  const allCountries: Country[] = useMemo(
    () =>
      (countriesData?.data ?? []).map((c) => ({
        countryId: c.countryId,
        englishName: c.englishName,
      })),
    [countriesData],
  );

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  /**
   * Update a top-level field in the local form.
   * For productCoverages, use dedicated coverage handlers below.
   */
  const handleChangeInput = (
    name: keyof AddProductForm,
    value: string | number | boolean | null | undefined,
  ) => {
    setForm((prev) => ({
      ...prev,
      [name]: value as any,
    }));
  };

  /** Toggle a required customer field in requiredData.requiredFields. */
  const handleChangeRequired = (name: RequiredDataType, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      requiredData: {
        ...prev.requiredData,
        requiredFields: checked
          ? [...prev.requiredData.requiredFields, name]
          : prev.requiredData.requiredFields.filter((r) => r !== name),
      },
    }));
  };

  /** Update specification root-level fields (e.g., dayCount, isUnlimitedData, isFup...). */
  const handleChangeSpecification = (
    name: keyof ProductUpsertRequest['specification'],
    value: string | boolean | number | null,
  ) => {
    setForm((prev) => ({
      ...prev,
      specification: {
        ...prev.specification,
        [name]: value as any,
      },
    }));
  };

  /** Update nested specification.dataCapacity fields. */
  const handleChangeSpecificationData = (
    name: keyof ProductUpsertRequest['specification']['dataCapacity'],
    value: number | string,
  ) => {
    setForm((prev) => ({
      ...prev,
      specification: {
        ...prev.specification,
        dataCapacity: {
          ...prev.specification.dataCapacity,
          [name]: value as any,
        },
      },
    }));
  };

  // ---------------------------------------------------------------------------
  // Coverage handlers (Autocomplete UI)
  // ---------------------------------------------------------------------------

  const handleAddCoverage = (country: Country) => {
    if (!country?.countryId) return;

    setForm((prev) => {
      const exists = prev.productCoverages.some((c) => c.countryId === country.countryId);
      if (exists) return prev;
      return { ...prev, productCoverages: [...prev.productCoverages, country] };
    });
  };

  const handleDeleteCoverage = (countryId: number) => {
    setForm((prev) => ({
      ...prev,
      productCoverages: prev.productCoverages.filter((c) => c.countryId !== countryId),
    }));
  };

  // ---------------------------------------------------------------------------
  // Navigation & actions
  // ---------------------------------------------------------------------------

  /**
   * Cancel flow (guarded)
   * - Use shared confirm dialog to prevent accidental loss.
   */
  const handleCancel = async () => {
    const ok = await confirm({
      priority: 'warning',
      title: 'Cancel?',
      description: 'Do you want to cancel and go back?',
      confirmText: 'Yes, cancel',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  };

  /**
   * Submit flow
   * - Convert UI form to API payload
   * - Validate required fields + apiParameter JSON format
   */
  const handleAdd = async () => {
    const payload: ProductUpsertRequest = {
      ...form,
      productCoverages: form.productCoverages.map((c) => ({ countryId: c.countryId })),
    };

    // Required fields validation (shared utility)
    if (alertMissingValues(payload)) return;

    // apiParameter must be null/undefined or a valid JSON string
    if (!isNullOrValidJson('apiParameter', payload.apiParameter)) return;

    try {
      const { message } = await createProduct(payload);

      if (message === 'OK') {
        systemAlert.success('Product added successfully.');
        navigate('/products');
        return;
      }

      systemAlert.error(message || 'Failed to add product.');
    } catch {
      systemAlert.error('Failed to add product.');
    }
  };

  // ---------------------------------------------------------------------------
  // Effects: stock options / API parameter template
  // ---------------------------------------------------------------------------

  // Build stock selector options once the list is loaded.
  useAsyncEffect(async () => {
    if (!stocksData) return;

    const sorted = [...stocksData.data]
      .map((s) => ({ label: s.stockCode, value: s.stockId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setStockOptionList(sorted);
  }, [stocksData]);

  // If stock changes, determine whether apiParameter is needed and set a template (supplier-specific).
  useAsyncEffect(async () => {
    if (!form.stockId) {
      setIsApiInputNeeded(false);
      setDefaultApiParameter(null);
      return;
    }

    const { data } = await getStock(form.stockId);
    const stock = data?.[0];
    if (!stock) return;

    const apiNeeded = stock.supplierTaskType === StockSupplierTaskType.API;
    setIsApiInputNeeded(apiNeeded);

    if (!apiNeeded) {
      setDefaultApiParameter(null);
      return;
    }

    // Supplier-specific JSON templates (operator guidance)
    const supplierName = stock.supplier.supplierName;

    let template: string | null = null;
    if (supplierName === 'CMI') template = JSON.stringify({ bundleId: '' }, null, 2);
    else if (supplierName === 'Uhuibao') template = JSON.stringify({ packageId: '' }, null, 2);
    else if (supplierName === 'Joytel' || supplierName === 'Joytel(Redeem)') {
      template = JSON.stringify({ productCode: '' }, null, 2);
    } else if (supplierName === 'TJ_USA') {
      template = JSON.stringify({ carrier: '', plan: '', day: '' }, null, 2);
    } else if (supplierName.startsWith('LG')) {
      template = JSON.stringify({ productCode: '' }, null, 2);
    } else if (supplierName.startsWith('SKT_KBOOKS')) {
      template = JSON.stringify({ rentalFeeProdId: '' }, null, 2);
    } else if (supplierName.startsWith('BillionConnect')) {
      template = JSON.stringify({ sku: '', copies: 0 }, null, 2);
    } else if (supplierName.startsWith('JCB')) {
      template = JSON.stringify({ group: '', sku: '', days: 0 }, null, 2);
    } else if (supplierName.startsWith('ORANGE')) {
      template = JSON.stringify({ zone: '' }, null, 2);
    }

    setDefaultApiParameter(template);

    // Prefill only when operator hasn't entered a custom value yet.
    if (template && !form.apiParameter) {
      setForm((prev) => ({ ...prev, apiParameter: template ?? undefined }));
    }
  }, [form.stockId]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const requiredFields = form.requiredData?.requiredFields ?? [];

  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ py: 2, maxWidth: 900, width: '100%', mr: 'auto' }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        {/* 1) Basic information */}
        <ProductBasicInfoSection
          productCode={form.productCode}
          productName={form.productName}
          description={form.description}
          price={form.price}
          onChangeName={(v) => handleChangeInput('productName', v)}
          onChangeDescription={(v) => handleChangeInput('description', v)}
          onChangePrice={(v) => handleChangeInput('price', v)}
        />

        {/* 2) Specification & coverage */}
        <ProductSpecificationSection
          specification={form.specification}
          coverages={form.productCoverages}
          allCountries={allCountries}
          onChangeSpecification={handleChangeSpecification}
          onChangeSpecificationData={handleChangeSpecificationData}
          onAddCoverage={handleAddCoverage}
          onDeleteCoverage={handleDeleteCoverage}
        />

        {/* 3) Settings (sale type, status, required fields, stock/API parameter) */}
        <ProductSettingsSection
          productStatus={form.productStatus}
          productSaleType={form.productSaleType}
          productType={form.productType}
          requiredFields={requiredFields}
          stockId={form.stockId || null}
          stockOptions={stockOptionList}
          apiParameter={form.apiParameter ?? defaultApiParameter ?? null}
          isApiInputNeeded={isApiInputNeeded}
          onChangeStatus={(value: ProductStatus) => handleChangeInput('productStatus', value)}
          onChangeSaleType={(value: ProductSaleType) => handleChangeInput('productSaleType', value)}
          onChangeProductType={(value: ProductType) => handleChangeInput('productType', value)}
          onToggleRequiredField={(field: RequiredDataType, checked: boolean) =>
            handleChangeRequired(field, checked)
          }
          onChangeStockId={(value: number | null) => handleChangeInput('stockId', value === null ? null : value)}
          onChangeApiParameter={(value: string | null) => handleChangeInput('apiParameter', value ?? undefined)}
        />

        {/* Footer actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<Close />}
            onClick={handleCancel}
            sx={ACTION_BTN_SX}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveOutlined />}
            onClick={handleAdd}
            sx={ACTION_BTN_SX}
          >
            Add product
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AddProduct;
